import React, { useEffect, useRef, useState, useCallback } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTranslation } from "react-i18next";

import { CanvasEditor } from "../../components/exercise-canvas/canvas-editor";
import ImageFilePreview from "../../components/shared/imageFilePreview";

import { selectStyles } from "../../components/shared/styles";

const ExerciseSceneEditor = ({
  diagram_data,
  image,
  width = 458,
  height = 458,
  children,
  actionTypes = [],
  onChange,
  ...props
}) => {
  const animatedComponents = makeAnimated();
  const { t } = useTranslation();
  const EditorLib = useRef(null);

  if (EditorLib.current === null) {
    EditorLib.current = new CanvasEditor();
  }
  const Editor = EditorLib.current;

  const field_items = [
    "cone",
    "ball",
    "goal",
    "player",
    "player_with_ball",
    "dribble",
    "transition",
    "press",
    "pass",
    "physical_activity_area",
    "indicator",
    "divider",
    "hotspot",
  ];
  const canvasRef = useRef(null);
  const [currentObject, setCurrentObject] = useState(undefined);
  const [data, setData] = useState(null);

  const escFunction = useCallback(event => {
    if (event.key === "Escape") {
      Editor.DeselectElement();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    Editor.subscribe("editor_data_change", setData);
    Editor.subscribe("selected_element", setCurrentObject);

    return () => {
      Editor.unsubscribe("editor_data_change", setData);
      Editor.unsubscribe("editor_data_change", setCurrentObject);
    };
  }, []);

  // Redraw canvas on data change
  useEffect(() => {
    Editor.initEditor({ data, canvas: canvasRef.current });
  }, [data, canvasRef, currentObject]);

  const filterDiagramData = () => {
    if (!diagram_data) return;
    let filteredData = parseDiagramData(diagram_data);
    if (image) {
      filteredData = {
        ...filteredData,
        transparent: true,
        objects: filteredData?.objects?.filter(item => !!item.hotspot_type),
      };
    } else {
      filteredData = { ...filteredData, transparent: false };
    }

    setData(filteredData);
  };

  const parseDiagramData = diagram => {
    return (typeof diagram_data === "string") ? JSON.parse(diagram_data) : diagram;
  };

  useEffect(() => {
    if (!diagram_data || data?.version == diagram_data?.version) return;
    filterDiagramData();
  }, [diagram_data]);

  useEffect(() => {
    filterDiagramData();
  }, [image]);

  // Redraw canvas on data change
  useEffect(() => {
    if (image) {
      const diagram = parseDiagramData(diagram_data);
      onChange &&
        onChange({ ...data, objects: [...diagram?.objects?.filter(item => !item.hotspot_type), ...data?.objects] });
    } else {
      onChange && onChange(data);
    }
  }, [data]);

  const updateSelectedObject = (key, value) => {
    const newObjects = [...data.objects];
    newObjects.splice(currentObject, 1, { ...newObjects[currentObject], [key]: value });
    setData({ ...data, objects: newObjects });
  };

  return (
    <div className="exercise-editor">
      <div className="exercise-editor-scene">
        <div className="heading">{t("exercises.editor.image")}</div>
        <div className="exercise-diagram-container" style={{ height, width }}>
          {image && (
            <ImageFilePreview image={image} className="exercise-diagram-container-image" style={{ height, width }} />
          )}
          <canvas className="exercise-diagram-container-canvas" ref={canvasRef} {...{ width, height, ...props }}>
            {t("exercises.editor.preview")}
          </canvas>
        </div>
        <div className="position-container">
          <div className="position-indicator"></div>
        </div>
      </div>
      <div className="exercise-editor-actions">
        <div className="exercise-editor-actions-top">
          <div className="heading">{t("exercises.editor.add")}</div>

          {!image ? (
            <>
              <Select
                className="button button-add no-padding"
                closeMenuOnSelect={true}
                components={animatedComponents}
                options={field_items.map(item => ({ value: item, label: t(`exercises.editor.add_element.${item}`) }))}
                placeholder={t("exercises.editor.placeholder")}
                onChange={selected => Editor.AddElement(selected.value)}
                styles={customStyles}
                value={null}
                isSearchable={false}
              />
              {currentObject !== undefined && Array.isArray(data?.objects?.[currentObject]?.points) && (
                <button className="button button-delete" onClick={() => Editor.RemovePoint()}>
                  {t("exercises.editor.remove_last_point")}
                </button>
              )}

              {currentObject !== undefined && data?.objects?.[currentObject]?.type === "hotspot" && (
                <Select
                  closeMenuOnSelect={true}
                  components={animatedComponents}
                  options={actionTypes.map(item => ({
                    value: item,
                    label: t(`exercises.editor.action_types.${item}`),
                  }))}
                  placeholder={t("exercises.editor.placeholder")}
                  onChange={selected => updateSelectedObject("hotspot_type", selected.value)}
                  value={{
                    value: data.objects[currentObject].hotspot_type,
                    label: t(`exercises.editor.action_types.${data.objects[currentObject].hotspot_type}`),
                  }}
                />
              )}

              {currentObject !== undefined && data?.objects?.[currentObject]?.type === "indicator" && (
                <Select
                  closeMenuOnSelect={true}
                  components={animatedComponents}
                  options={["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"].map(value => ({ value, label: value }))}
                  onChange={selected => updateSelectedObject("marker", selected.value)}
                  value={{ value: data.objects[currentObject].marker, label: data.objects[currentObject].marker }}
                  styles={selectStyles}
                />
              )}

              {currentObject !== undefined && data?.objects?.[currentObject]?.variation && (
                <Select
                  closeMenuOnSelect={true}
                  components={animatedComponents}
                  options={["red", "blue", "green", "yellow"].map(value => ({
                    value,
                    label: t(`exercises.colors.${value}`),
                  }))}
                  onChange={selected => updateSelectedObject("variation", selected.value)}
                  value={{
                    value: data.objects[currentObject].variation,
                    label: t(`exercises.colors.${data.objects[currentObject].variation}`),
                  }}
                  styles={selectStyles}
                />
              )}
              {currentObject !== undefined && data?.objects?.[currentObject]?.type === "goal" && (
                <Select
                  closeMenuOnSelect={true}
                  components={animatedComponents}
                  options={["small", "large"].map(value => ({ value, label: t(`exercises.size.${value}`) }))}
                  onChange={selected => updateSelectedObject("size", selected.value)}
                  value={{
                    value: data.objects[currentObject].size,
                    label: t(`exercises.size.${data.objects[currentObject].size}`),
                  }}
                  styles={selectStyles}
                />
              )}
              {currentObject !== undefined && data?.objects?.[currentObject]?.type === "dribble" && (
                <button className="button button-add" onClick={() => Editor.AddModifier()}>
                  {t("buttons.exercise.add_modifier")}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                className="button button-add no-padding"
                onClick={() => {
                  Editor.AddElement("hotspot");
                }}
              >
                {t("buttons.exercise.add_hotspot")}
              </button>
              {currentObject !== undefined && data?.objects?.[currentObject]?.type === "hotspot" && (
                <Select
                  closeMenuOnSelect={true}
                  components={animatedComponents}
                  options={actionTypes.map(item => ({
                    value: item,
                    label: t(`exercises.editor.action_types.${item}`),
                  }))}
                  placeholder={t("exercises.editor.placeholder")}
                  onChange={selected => updateSelectedObject("hotspot_type", selected.value)}
                  value={{
                    value: data.objects[currentObject].hotspot_type,
                    label: t(`exercises.editor.action_types.${data.objects[currentObject].hotspot_type}`),
                  }}
                />
              )}
            </>
          )}
          {currentObject !== undefined && (
            <button className="button button-add" onClick={() => Editor.DeselectElement()}>
              {t("common.done")}
            </button>
          )}
        </div>
        <div
          className="exercise-editor-actions-bottom"
          style={{ display: currentObject === undefined ? "block" : "none" }}
        >
          {children}
        </div>
      </div>
      <div className="exercise-editor-objects">
        <div className="heading">{t("exercises.editor.added_objects")}</div>
        <div className="exercise-editor-objects-list">
          {data?.objects?.map((item, index) => (
            <div
              key={index}
              className={`exercise-editor-objects-list-item ${index === currentObject ? "current" : ""}`}
              onClick={() => Editor.SelectElement(index)}
              onMouseEnter={() => Editor.HighlightElement(index)}
            >
              <div>
                <span className="item-type-name">{t(`exercises.editor.add_element.${item?.type}`) || item?.type}</span>
                {item?.size && <span className="variation">{t(`exercises.size.${item.size}`)}</span>}
                {item?.variation && (
                  <span className={`variation variation-${item?.variation}`}>
                    {t(`exercises.colors.${item.variation}`)}
                  </span>
                )}
                {item?.marker && <span className="marker">{item.marker}</span>}
                {item?.modifiers?.length > 0 && (
                  <span className="marker">
                    {item.modifiers.length} modifier{item.modifiers.length > 1 ? "s" : ""}
                  </span>
                )}
                {item?.hotspot_type && (
                  <span className="marker">
                    {t(`exercises.editor.action_types.${item?.hotspot_type}`) || item?.hotspot_type}
                  </span>
                )}
                {currentObject === index &&
                  item?.modifiers?.length > 0 &&
                  item?.modifiers.map((m, mIndex) => (
                    <div className="modifiers" key={mIndex} onMouseEnter={() => Editor.HighlightModifier(mIndex)}>
                      <div>{t("exercises.editor.modifier")}</div>
                      <div
                        className="fa fa-trash-o"
                        onClick={e => e.stopPropagation() & Editor.RemoveModifier(mIndex)}
                      ></div>
                    </div>
                  ))}
              </div>
              <i className="fa fa-trash-o" onClick={e => e.stopPropagation() & Editor.RemoveElement(index)}></i>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExerciseSceneEditor;

const customStyles = {
  control: (provided, state) => ({ ...provided, backgroundColor: "none", border: "none", boxShadow: "none" }),
  placeholder: (provided, state) => ({ ...provided, color: "#000" }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  option: provided => ({ ...provided, padding: "5px 10px", fontSize: 14 }),
};
